import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { capstoneApi } from '../services/capstoneApi';
import { ProjectSummary, ProjectStatus } from '../types/capstone';

const STATUS_DOT: Record<ProjectStatus, string> = {
  shaping: 'bg-blue-500',
  building: 'bg-yellow-500',
  ready: 'bg-green-500',
  paused: 'bg-gray-400',
  archived: 'bg-gray-300',
  failed: 'bg-red-500',
  deleted: 'bg-red-700',
};

const STACK_LABEL: Record<string, string> = {
  'python-data': 'Python · Data',
  'node-next': 'Next.js · Web',
  'rust-cli': 'Rust · CLI',
};

// Plain-language starter ideas — no technical terms. Students describe what they
// want; Capstone figures out the language/approach.
const STARTER_IDEAS = [
  'A snake game I can play in the browser',
  'A quiz game about world capitals',
  'A number-guessing game with hints',
  'Tic-tac-toe against the computer',
  'Turn a list of my monthly expenses into a chart',
  'A to-do list app that remembers my tasks',
];

const formatRelative = (iso: string): string => {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const sec = Math.max(0, Math.floor((now - then) / 1000));
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
};

const IntentPrompt: React.FC<{
  busy: boolean;
  initialText?: string;
  onSubmit: (text: string) => void;
}> = ({ busy, initialText = '', onSubmit }) => {
  const [text, setText] = useState(initialText);
  return (
    <div className="max-w-2xl mx-auto px-6 mt-16">
      <h1 className="text-3xl font-bold text-foreground mb-2">Capstone</h1>
      <p className="text-muted-foreground mb-8">
        Describe a project you want to build. Cubemate will plan it, scaffold it,
        and build it for you. You direct via chat — the AI writes the code.
      </p>

      <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-4">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={5}
          placeholder={'e.g. "A game where a spaceship dodges asteroids", or "an app that turns my monthly expenses into a chart"'}
          className="w-full resize-none rounded-lg bg-transparent text-base text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none"
        />
        <div className="flex justify-between items-center mt-3">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Say it in plain words — no coding terms needed. Capstone works out the how (and asks if it's unsure).
          </span>
          <button
            type="button"
            onClick={() => {
              const t = text.trim();
              if (t && !busy) onSubmit(t);
            }}
            disabled={busy || !text.trim()}
            className="px-5 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium"
          >
            {busy ? 'Creating…' : 'Start project →'}
          </button>
        </div>
      </div>

      <div className="mt-4">
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Need an idea? Tap one to get started:</p>
        <div className="flex flex-wrap gap-2">
          {STARTER_IDEAS.map((idea) => (
            <button
              key={idea}
              type="button"
              onClick={() => setText(idea)}
              className="text-xs px-3 py-1.5 rounded-full border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-purple-400 hover:text-purple-700 dark:hover:text-purple-300 transition-colors"
            >
              {idea}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const ProjectCard: React.FC<{
  project: ProjectSummary;
  onOpen: (projectId: string) => void;
  onDelete: (projectId: string, title: string) => void;
}> = ({ project, onOpen, onDelete }) => {
  const stackLabel = project.stack ? STACK_LABEL[project.stack] ?? project.stack : 'Stack TBD';
  return (
    <div className="relative group rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 hover:border-purple-400 dark:hover:border-purple-500 transition-colors shadow-sm hover:shadow-md">
      <button
        type="button"
        onClick={() => onOpen(project.project_id)}
        className="w-full text-left p-4"
      >
        <div className="flex items-center justify-between mb-2 pr-7">
          <span className="text-xs uppercase tracking-wide text-purple-600 dark:text-purple-400 font-medium">
            {stackLabel}
          </span>
          <span
            title={project.status}
            className={`inline-block w-2 h-2 rounded-full ${STATUS_DOT[project.status] ?? 'bg-gray-400'}`}
          />
        </div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1 line-clamp-2 pr-6">
          {project.title || 'Untitled project'}
        </h3>
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mt-3">
          <span>iter {project.iteration_count}</span>
          <span>{formatRelative(project.last_accessed_at)}</span>
        </div>
      </button>
      <button
        type="button"
        title="Delete project"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(project.project_id, project.title || 'this project');
        }}
        className="absolute top-2 right-2 w-7 h-7 rounded-md flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
      >
        ✕
      </button>
    </div>
  );
};

const NewProjectTile: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="text-left rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 hover:border-purple-400 dark:hover:border-purple-500 transition-colors p-4 min-h-[140px] flex items-center justify-center text-gray-600 dark:text-gray-400"
  >
    <div className="text-center">
      <div className="text-3xl mb-1">+</div>
      <div className="text-sm font-medium">New Capstone</div>
    </div>
  </button>
);

const CapstonePage: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [showIntent, setShowIntent] = useState(false);
  const [initialIntent, setInitialIntent] = useState('');
  const [pathCtx, setPathCtx] = useState<{ pathId: string; nodeId: string } | null>(null);

  // Learning Path "Start →" deep links arrive as /capstone?prompt=...&path=&node=.
  // Land straight on the intent form, prefilled — NOT the "My Capstones" grid —
  // so the student just reviews and hits "Start project →" (same pattern as
  // /bookmate and /codemate prefill). path/node are kept for learning_event
  // attribution on create (paths step 3).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const promptParam = params.get('prompt');
    const pathParam = params.get('path');
    const nodeParam = params.get('node');
    if (promptParam) {
      setInitialIntent(promptParam);
      if (pathParam && nodeParam) setPathCtx({ pathId: pathParam, nodeId: nodeParam });
      setShowIntent(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    let active = true;
    capstoneApi.listProjects()
      .then((res) => {
        if (!active) return;
        setProjects(res.projects);
      })
      .catch((err) => {
        if (!active) return;
        setLoadError(err?.message || 'Failed to load projects.');
        setProjects([]);
      });
    return () => { active = false; };
  }, []);

  const handleDelete = async (projectId: string, displayName: string) => {
    const ok = window.confirm(
      `Delete "${displayName}"?\n\nThis destroys the project + any active container. Cannot be undone.`,
    );
    if (!ok) return;
    try {
      await capstoneApi.deleteProject(projectId);
    } catch (err: any) {
      // 404 = already gone (deleted elsewhere / stale list) — treat as success.
      if (!String(err?.message || '').includes('404')) {
        setLoadError(err?.message || 'Failed to delete project.');
        return;
      }
    }
    // Remove from the list on success OR already-gone.
    setProjects((prev) => (prev ? prev.filter((p) => p.project_id !== projectId) : prev));
  };

  const handleCreate = async (problem: string) => {
    setCreating(true);
    try {
      const project = await capstoneApi.createProject(problem, pathCtx);
      // Pass the problem statement as navigation state so the project page
      // auto-sends it as the first iteration message — no need for the user
      // to re-type or re-press Send.
      navigate(`/capstone/${project.project_id}`, {
        state: { autoStartMessage: problem },
      });
    } catch (err: any) {
      setLoadError(err?.message || 'Failed to create project.');
      setCreating(false);
    }
  };

  // Empty-state OR explicit "+ New" click → render the intent prompt full-page.
  const isEmpty = projects !== null && projects.length === 0;
  const shouldShowIntent = isEmpty || showIntent;

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />
      <div className="flex-1 overflow-y-auto">
        {projects === null && !loadError && (
          <div className="flex items-center justify-center h-full text-gray-500">
            Loading…
          </div>
        )}

        {loadError && (
          <div className="max-w-2xl mx-auto mt-16 px-6">
            <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-4 text-sm text-red-700 dark:text-red-300">
              {loadError}
            </div>
          </div>
        )}

        {projects && !loadError && shouldShowIntent && (
          <IntentPrompt busy={creating} initialText={initialIntent} onSubmit={handleCreate} />
        )}

        {projects && !loadError && !shouldShowIntent && (
          <div className="max-w-5xl mx-auto px-6 py-8">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-2xl font-bold text-foreground">My Capstones</h1>
              <button
                type="button"
                onClick={() => setShowIntent(true)}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium"
              >
                + New Capstone
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <NewProjectTile onClick={() => setShowIntent(true)} />
              {projects.map((p) => (
                <ProjectCard
                  key={p.project_id}
                  project={p}
                  onOpen={(id) => navigate(`/capstone/${id}`)}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CapstonePage;
